<script lang="ts" setup>
import { ref } from 'vue';
import { ButtonComp, ContentComp, DisplayPhone, SearchBox, ValidatedInt } from '@/components';
import { useGuiStore, useToastStore } from '@/stores';
import type { RouteParams } from '@/objects/RouteParams';
import { useKeyboardListener, useLoaders, useTaskLoader } from '@/composeables';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { Participant, ParticipantSearchRequest, SearchRequest } from '@/objects/generated';
import { ParticipantDao } from '@/dao';
import ListComp from '@/components/List/ListComp.vue';
import type ListCompExpose from '@/objects/ListCompExpose';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { propertyof } from '@/utilities';
import Participant2FARenderer from './Participant2FARenderer.vue';

// A ref to the list component
const listcomp = ref<ListCompExpose<Participant> | null>(null);

const disableSearch = ref<boolean>(false);
const searchText = ref<string>('');
const isSearchTextValid = ref<boolean>(true);

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['COUNTRIES']);

// The list of all the races
const participants = ref<Array<Participant>>([]);

// This is the initial state of the loader
// Whenever we have loaded all the data we will set this to true so any later loaders will be shown as a modal on top of the content
const showLoaderAsModal = ref<boolean>(true);

/**
 * We add the custom columns to the list of columns.
 * @param columns
 */
const addCustomColumns = (columns: GridColumnOptions<Participant>[]) => {
    /**
     * We add some extra columns for superadmin search
     * @param event
     */
    new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('ID'), 'account.id', 'userid')
        .ReadOnly()
        .CellRenderer(ValidatedInt, { format: '3digit' })
        .Size(80, 80)
        .Insert(columns, 1);
    new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Email'), 'contestant.email').Size(120).InsertAtEnd(columns);
    new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Phone'), 'contestant.phone')
        .Size(120)
        .CellRenderer(DisplayPhone)
        .InsertAtEnd(columns);
    new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Username'), 'account.username')
        .Size(120)
        .ReadOnly()
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Current2FAState'), 'account.disabletwofactor', 'disabletwofactor')
        .ReadOnly()
        .SortDisabled()
        .Size(230, 230)
        .CellRenderer(Participant2FARenderer)
        .InsertAtEnd(columns);

    return columns;
};

/**
 * Search for participants
 * @param searchText
 */
const onSearchClick = (e: KeyboardEvent) => {
    // if the key is tab, we do not want to search
    if (e.key === 'Tab') {
        return;
    }

    isSearchTextValid.value = searchText.value.trim().length >= 5;

    if (!isSearchTextValid.value) {
        useToastStore().addWarning('general.searchtexttooshort');
        return;
    }

    AddLoader(async () => {
        await doSearch();
        listcomp.value?.dataUpdated();
    }, '');
};

/**
 * Do the search
 */
const doSearch = async () => {
    const search = ParticipantSearchRequest.FromJsValues({
        Query: searchText.value ?? '',
        SearchType: 'Contains',
        Maxitems: 99999,
    });

    participants.value = await ParticipantDao.ParticipantSearch(search);

    listcomp.value?.dataUpdated();

    // We focus the first row in the list
    listcomp.value?.focusfirst();
};

/**
 * Save the participants
 */
const saveParticipants = async () => {
    let changedParticipants = participants.value.filter((p) => p.EntityState !== 'Unchanged') ?? [];

    if (changedParticipants.length === 0) {
        useToastStore().addInfo('general.nothingtosave');
        return;
    }

    AddLoader(async () => {
        try {
            await ParticipantDao.SaveParticipants(changedParticipants);

            // We rerun the search to get the latest data
            await doSearch();

            // Reset the changes
            listcomp.value?.clearChanges();
        } catch (e: any) {
            useToastStore().addError(e.message);
            throw e;
        }
    }, '');
};

/**
 * Callback when the visibility of the component changes
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
    if (isVisible) {
        useKeyboardListener().addKeyboardShortcut({ alias: 'participantlistview', on: 'CTRL+S', action: saveParticipants });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('participantlistview');
    }
};
</script>

<template>
    <ContentComp
        class="h-100"
        :loadState="LoadState"
        :show-as-modal="showLoaderAsModal"
        @is-visible="onIsVisible"
    >
        <ListComp
            class="p-0"
            v-model="participants"
            :gui="useGuiStore().Participant"
            :readonly="true"
            :select-all="false"
            :hide-toolbar="true"
            :customColumns="addCustomColumns"
            ref="listcomp"
        >
            <template #toolbar>
                <div class="d-flex gap-2">
                    <ButtonComp
                        button-type="Save"
                        @click="saveParticipants"
                    ></ButtonComp>
                    <SearchBox
                        placeholder="general.searchplaceholder"
                        v-model="searchText"
                        class="w-100"
                        :disabled="disableSearch"
                        @keydown.enter="onSearchClick"
                    />
                </div>
            </template>
        </ListComp>
    </ContentComp>
</template>
